// ST-32 -Claude- Business portal API client for Nearrme.
// All shapes mirror the actual SalesTrack OpenAPI spec at
// https://app-salestrack-api-dev.azurewebsites.net/swagger/v1/swagger.json
// Real API only — no mocks, no stubs.

import { getApiClient } from '../api/client';

// =============================================================================
// SHAPES — copied verbatim from SalesTrack swagger.json schemas
// =============================================================================

export interface LoginRequest {
  username: string;
  password: string;
  deviceId?: string;
  deviceType?: string;
  fcmRegID?: string;
}

export interface AuthenticationResponse {
  success: boolean;
  token?: string | null;
  refreshToken?: string | null;
  expiresAt?: string | null;
  userId?: string | null;
  email?: string | null;
  userName?: string | null;
  role?: string | null;
  companyCode?: string | null;
  imagePath?: string | null;
  profileImageUrl?: string | null;
}

export interface CompanyViewModel {
  companyId?: number;
  companyCode?: string | null;
  companyName?: string | null;
  address?: string | null;
  phoneNumber?: string | null;
  email?: string | null;
  isActive?: boolean;
  legalName?: string | null;
  websiteUrl?: string | null;
  businessType?: string | null;
  logoUrl?: string | null;
  notes?: string | null;
  publicUrlDecorator?: string | null;
  assignedUserEmails?: string[] | null;
}

export interface UpsertCompanyProfileRequest {
  companyCode?: string;
  companyName: string;
  legalName?: string;
  address?: string;
  phoneNumber?: string;
  email?: string;
  websiteUrl?: string;
  businessType?: string;
  logoUrl?: string;
  notes?: string;
  publicUrlDecorator?: string;
  isActive: boolean;
  assignedUserEmails?: string[];
}

export interface BrandViewModel {
  brandId?: number;
  brandName?: string | null;
  description?: string | null;
  category?: string | null;
  imageName?: string | null;
  companyCode?: string | null;
  sort?: number;
  isActive?: boolean;
}

export interface ProductViewModel {
  productId?: number;
  productName?: string | null;
  description?: string | null;
  brandId?: number | null;
  brandName?: string | null;
  productCategoryId?: number | null;
  productCategoryName?: string | null;
  defaultPrice?: number | null;
  salesPrice?: number | null;
  inStock?: boolean;
  availableQuantity?: number;
  availabilityStatus?: string | null;
  availabilityComment?: string | null;
  isActive?: boolean;
  companyCode?: string | null;
}

export interface StoreViewModel {
  storeId: number;
  companyCode: string;
  name: string;
  latitude: number;
  longitude: number;
  openingHours?: string | null;
  languagesSpoken: string;
  paymentMethods: string;
  rating: number;
  isActive: boolean;
  productCount: number;
  availableUnits: number;
}

export interface UserProfileViewModel {
  userProfileId?: number;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  phoneNumber?: string | null;
  companyCode?: string | null;
  role?: string | null;
  isActive?: boolean;
  createdDate?: string | null;
  imageUrl?: string | null;
  assignedCompanyCodes?: string[] | null;
}

export interface CreateUserProfileRequest {
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  companyCode: string;
  role: string;
  imageUrl?: string;
  assignedCompanyCodes?: string[];
}

export interface UpdateUserProfileRequest extends CreateUserProfileRequest {
  isActive: boolean;
}

export interface ConsumerOrderSummaryViewModel {
  consumerOrderId: number;
  productName: string;
  brandName?: string | null;
  storeName?: string | null;
  companyCode: string;
  totalInr: number;
  fulfilmentMode: string;
  createdAtUtc: string;
}

export interface ConsumerAdminViewModel {
  consumerId: number;
  name: string;
  email: string;
  phoneNumber: string;
  createdDate: string;
  orderCount: number;
  totalSpendInr: number;
  orders: ConsumerOrderSummaryViewModel[];
}

export interface SecurityAuditLogViewModel {
  id?: number;
  sessionUserId?: number | null;
  eventType?: string | null;
  description?: string | null;
  ipAddress?: string | null;
  userAgent?: string | null;
  createdDate?: string | null;
  email?: string | null;
  role?: string | null;
  companyCode?: string | null;
}

export interface RoleViewModel {
  roleCode: string;
  displayName: string;
  description: string;
  profileType: string;
  scopeLevel: string;
  profileSummary: string;
  permissions: string[];
  permissionCodes?: string[];
  adminOnly: boolean;
  consumerRole: boolean;
}

export interface PermissionViewModel {
  permissionCode: string;
  displayName: string;
  category: string;
  description: string;
  screenPath: string;
}

export interface AgentStatusResponse {
  providers: Array<{
    name: string;
    isAvailable: boolean;
    lastChecked?: string;
    model?: string;
  }>;
}

export interface AgentUsageStats {
  totalRequests?: number;
  totalInputTokens?: number;
  totalOutputTokens?: number;
  totalCostUsd?: number;
  byProvider?: Record<string, { requests: number; inputTokens: number; outputTokens: number; costUsd: number }>;
  byCompany?: Record<string, { requests: number; costUsd: number }>;
}

export interface AdminOrderViewModel {
  consumerOrderId: number;
  productId: number;
  productName?: string | null;
  brandName?: string | null;
  storeId?: number | null;
  storeName?: string | null;
  companyCode?: string | null;
  consumerId?: number | null;
  consumerName?: string | null;
  consumerEmail?: string | null;
  quantity: number;
  totalInr: number;
  fulfilmentMode: string;
  orderStatus?: string | null;
  orderChannel?: string | null;
  serviceMethod?: string | null;
  attributedSalespersonId?: number | null;
  warrantyRecordId?: string | null;
  createdAtUtc: string;
}

export interface LoginOptionsState {
  emailPasswordEnabled: boolean;
  emailCodeEnabled: boolean;
  phoneCodeEnabled: boolean;
  googleEnabled: boolean;
  passkeyEnabled: boolean;
}

// =============================================================================
// API CALLS — all use the singleton client (handles token + telemetry headers)
// =============================================================================

const STORAGE_TOKEN_KEY = 'nearrme.admin.token';
const STORAGE_USER_KEY = 'nearrme.admin.user';

export function getAdminToken(): string | null {
  if (typeof window === 'undefined') return null;
  return localStorage.getItem(STORAGE_TOKEN_KEY);
}

export function getAdminUser(): AuthenticationResponse | null {
  if (typeof window === 'undefined') return null;
  const raw = localStorage.getItem(STORAGE_USER_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function setAdminAuth(auth: AuthenticationResponse) {
  if (!auth.token) throw new Error('Login response missing token');
  localStorage.setItem(STORAGE_TOKEN_KEY, auth.token);
  localStorage.setItem(STORAGE_USER_KEY, JSON.stringify(auth));
}

export function clearAdminAuth() {
  localStorage.removeItem(STORAGE_TOKEN_KEY);
  localStorage.removeItem(STORAGE_USER_KEY);
}

export function isBusinessPortalRole(role?: string | null): boolean {
  if (!role) return false;
  const roles = role
    .split(/[,\s;|]+/)
    .map((value) => value.trim())
    .filter(Boolean);

  return roles.some((value) =>
    [
      'Admin',
      'SuperAdmin',
      'NearrmeAdmin',
      'NearrmeUser',
      'Business',
      'BusinessAdmin',
      'BusinessOwner',
      'CompanyOwner',
      'CompanyAdmin',
      'CompanyManager',
      'StoreOwner',
      'StoreManager',
    ].includes(value)
  );
}

export function isNearrmeAdminRole(role?: string | null): boolean {
  if (!role) return false;
  return role
    .split(/[,\s;|]+/)
    .map((value) => value.trim())
    .some((value) => ['Admin', 'SuperAdmin', 'NearrmeAdmin'].includes(value));
}

/** Build a fetch-style URL against the API base */
function apiUrl(path: string): string {
  const base = (import.meta.env.VITE_API_URL ?? 'https://app-salestrack-api-dev.azurewebsites.net').replace(/\/$/, '');
  return `${base}${path}`;
}

/** Lightweight typed fetch wrapper used by the admin pages. Handles auth, errors. */
async function adminFetch<T>(
  path: string,
  init: RequestInit = {}
): Promise<T> {
  const headers: Record<string, string> = {
    'Accept': 'application/json',
    'Content-Type': 'application/json',
    'X-App-Id': 'nearrme-admin-web',
    ...((init.headers as Record<string, string>) ?? {}),
  };
  const token = getAdminToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const res = await fetch(apiUrl(path), { ...init, headers });
  if (res.status === 401) {
    clearAdminAuth();
    throw new Error('Session expired. Please log in again.');
  }
  if (!res.ok) {
    let detail = '';
    try {
      const body = await res.json();
      detail = body.message ?? body.Message ?? body.error ?? body.title ?? '';
    } catch {
      detail = res.statusText;
    }
    // ST-1290 -Claude- never eat
    console.error('[admin api]', res.status, path, detail);
    throw new Error(`HTTP ${res.status}: ${detail || 'Request failed'}`);
  }
  return res.json() as Promise<T>;
}

// -----------------------------------------------------------------------------
// Auth
// -----------------------------------------------------------------------------

export async function login(username: string, password: string): Promise<AuthenticationResponse> {
  // POST /api/Account/Login per swagger
  const body: LoginRequest = {
    username,
    password,
    deviceId: 'nearrme-admin-web',
    deviceType: 'Web',
  };
  const res = await fetch(apiUrl('/api/account/login'), {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'X-App-Id': 'nearrme-admin-web',
    },
    body: JSON.stringify(body),
  });
  if (res.status === 401) throw new Error('Invalid email or password.');
  if (res.status === 423) throw new Error('Account temporarily locked. Try again later.');
  if (res.status === 429) throw new Error('Too many login attempts. Wait a minute.');
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const j = await res.json();
      msg = j.message ?? j.Message ?? msg;
    } catch {}
    throw new Error(msg);
  }
  const auth = (await res.json()) as AuthenticationResponse;
  if (!auth.success || !auth.token) {
    throw new Error('Login failed.');
  }
  if (!isBusinessPortalRole(auth.role)) {
    throw new Error('This account does not have business access yet.');
  }
  return auth;
}

export async function requestBusinessLoginCode(email: string): Promise<void> {
  const res = await fetch(apiUrl('/api/account/request-login-code'), {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'X-App-Id': 'nearrme-admin-web',
    },
    body: JSON.stringify({ email }),
  });
  if (!res.ok) throw new Error('Unable to send login code.');
}

export async function loginBusinessWithCode(email: string, code: string): Promise<AuthenticationResponse> {
  const res = await fetch(apiUrl('/api/account/login-with-code'), {
    method: 'POST',
    headers: {
      'Accept': 'application/json',
      'Content-Type': 'application/json',
      'X-App-Id': 'nearrme-admin-web',
    },
    body: JSON.stringify({ email, code }),
  });
  if (res.status === 401) throw new Error('Invalid email or code.');
  if (!res.ok) throw new Error(`HTTP ${res.status}: Login with code failed.`);
  const auth = (await res.json()) as AuthenticationResponse;
  if (!auth.success || !auth.token) throw new Error('Login failed.');
  if (!isBusinessPortalRole(auth.role)) throw new Error('This account does not have business access yet.');
  return auth;
}

export function getLoginOptions(): Promise<LoginOptionsState> {
  return fetch(apiUrl('/api/auth/login-options'), {
    headers: {
      'Accept': 'application/json',
      'X-App-Id': 'nearrme-admin-web',
    },
  }).then((res) => {
    if (!res.ok) throw new Error(`HTTP ${res.status}: Login options unavailable.`);
    return res.json() as Promise<LoginOptionsState>;
  });
}

export function updateLoginOptions(options: LoginOptionsState): Promise<LoginOptionsState> {
  return adminFetch<LoginOptionsState>('/api/auth/login-options', {
    method: 'PUT',
    body: JSON.stringify(options),
  });
}

export async function logout(): Promise<void> {
  try {
    await fetch(apiUrl('/api/account/logout'), {
      method: 'POST',
      headers: {
        'Accept': 'application/json',
        'Authorization': `Bearer ${getAdminToken() ?? ''}`,
      },
    });
  } catch (e) {
    console.warn('[admin api] logout request failed (non-fatal):', e);
  } finally {
    clearAdminAuth();
  }
}

// -----------------------------------------------------------------------------
// Companies — GET /api/Companies/search
// -----------------------------------------------------------------------------

export function listCompanies(): Promise<CompanyViewModel[]> {
  return adminFetch<CompanyViewModel[]>('/api/companies/search');
}

export function getCompany(companyCode: string): Promise<CompanyViewModel> {
  return adminFetch<CompanyViewModel>(`/api/companies/name/${encodeURIComponent(companyCode)}`);
}

export function createCompany(request: UpsertCompanyProfileRequest): Promise<CompanyViewModel> {
  return adminFetch<CompanyViewModel>('/api/companies', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export function updateCompany(companyCode: string, request: UpsertCompanyProfileRequest): Promise<CompanyViewModel> {
  return adminFetch<CompanyViewModel>(`/api/companies/${encodeURIComponent(companyCode)}`, {
    method: 'PUT',
    body: JSON.stringify(request),
  });
}

// -----------------------------------------------------------------------------
// Brands — GET /api/Brands?companyCode=…&isActive=true
// -----------------------------------------------------------------------------

export function listBrands(companyCode: string, isActive = true): Promise<BrandViewModel[]> {
  const params = new URLSearchParams({ companyCode, isActive: String(isActive) });
  return adminFetch<BrandViewModel[]>(`/api/brands?${params}`);
}

// -----------------------------------------------------------------------------
// Products — GET /api/Products/company/{companyCode}?isActive=true
// -----------------------------------------------------------------------------

export function listProducts(companyCode: string, isActive = true): Promise<ProductViewModel[]> {
  const params = new URLSearchParams({ isActive: String(isActive) });
  return adminFetch<ProductViewModel[]>(`/api/products/company/${encodeURIComponent(companyCode)}?${params}`);
}

export function listStores(companyCode: string, isActive = true): Promise<StoreViewModel[]> {
  const params = new URLSearchParams({ isActive: String(isActive) });
  return adminFetch<StoreViewModel[]>(`/api/stores/company/${encodeURIComponent(companyCode)}?${params}`);
}

export function listOrders(companyCode: string): Promise<AdminOrderViewModel[]> {
  const params = new URLSearchParams({ companyCode });
  return adminFetch<AdminOrderViewModel[]>(`/api/orders?${params}`);
}

// -----------------------------------------------------------------------------
// Users — GET /api/UserProfiles?companyCode=…
// -----------------------------------------------------------------------------

export function listUsers(companyCode: string): Promise<UserProfileViewModel[]> {
  const params = new URLSearchParams({ companyCode });
  return adminFetch<UserProfileViewModel[]>(`/api/userprofiles?${params}`);
}

export function listNearrmeTeam(): Promise<UserProfileViewModel[]> {
  return adminFetch<UserProfileViewModel[]>('/api/nearrme-team');
}

export function createUserProfile(request: CreateUserProfileRequest): Promise<UserProfileViewModel> {
  return adminFetch<UserProfileViewModel>('/api/userprofiles', {
    method: 'POST',
    body: JSON.stringify(request),
  });
}

export function updateUserProfile(userProfileId: number, request: UpdateUserProfileRequest): Promise<UserProfileViewModel> {
  return adminFetch<UserProfileViewModel>(`/api/userprofiles/${userProfileId}`, {
    method: 'PUT',
    body: JSON.stringify(request),
  });
}

export function listConsumers(companyCode: string): Promise<ConsumerAdminViewModel[]> {
  const params = new URLSearchParams({ companyCode });
  return adminFetch<ConsumerAdminViewModel[]>(`/api/consumers?${params}`);
}

export function listRoles(): Promise<RoleViewModel[]> {
  return adminFetch<RoleViewModel[]>('/api/roles');
}

export function listPermissions(): Promise<PermissionViewModel[]> {
  return adminFetch<PermissionViewModel[]>('/api/permissions');
}

export function updateRolePermissions(roleCode: string, permissionCodes: string[]): Promise<RoleViewModel> {
  return adminFetch<RoleViewModel>(`/api/roles/${encodeURIComponent(roleCode)}/permissions`, {
    method: 'POST',
    body: JSON.stringify({ permissionCodes }),
  });
}

export function updateUserRole(userProfileId: number, role: string, companyCode?: string | null): Promise<UserProfileViewModel> {
  return adminFetch<UserProfileViewModel>(`/api/userprofiles/${userProfileId}/role`, {
    method: 'POST',
    body: JSON.stringify({ role, companyCode }),
  });
}

// -----------------------------------------------------------------------------
// Logs — GET /api/SecurityAuditLog
// -----------------------------------------------------------------------------

export function listAuditLogs(sessionUserId?: number, createdDate?: string): Promise<SecurityAuditLogViewModel[]> {
  const params = new URLSearchParams();
  if (sessionUserId !== undefined) params.set('sessionUserId', String(sessionUserId));
  if (createdDate) params.set('createdDate', createdDate);
  const qs = params.toString();
  return adminFetch<SecurityAuditLogViewModel[]>(`/api/securityauditlog${qs ? '?' + qs : ''}`);
}

// -----------------------------------------------------------------------------
// Agents — GET /api/v2/agents/{status,health,usage-stats,costs}
// -----------------------------------------------------------------------------

export function getAgentStatus(): Promise<AgentStatusResponse> {
  return adminFetch<AgentStatusResponse>('/api/v2/agents/status');
}

export function getAgentHealth(): Promise<AgentStatusResponse> {
  return adminFetch<AgentStatusResponse>('/api/v2/agents/health');
}

export function getAgentUsageStats(fromDate?: string, toDate?: string, companyCode?: string): Promise<AgentUsageStats> {
  const params = new URLSearchParams();
  if (fromDate) params.set('fromDate', fromDate);
  if (toDate) params.set('toDate', toDate);
  if (companyCode) params.set('companyCode', companyCode);
  const qs = params.toString();
  return adminFetch<AgentUsageStats>(`/api/v2/agents/usage-stats${qs ? '?' + qs : ''}`);
}

export function getAgentConversations(): Promise<unknown[]> {
  return adminFetch<unknown[]>('/api/v2/agents/conversations');
}

// -----------------------------------------------------------------------------
// Suppress unused-import warning
// -----------------------------------------------------------------------------
void getApiClient;
